import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';

const ICON = 1.75;

/**
 * The journal «Værktøjer» column: heading plus a stack of collapsible cards.
 * Shared with the course-step workspace so both use the same chrome.
 */
export function ToolsColumn({
  title,
  onCollapse,
  collapseLabel,
  children,
  className,
  scroll = true,
  testId,
}: {
  title: string;
  onCollapse?: () => void;
  collapseLabel?: string;
  children: ReactNode;
  className?: string;
  /** Journal locks the column and scrolls inside it. The course step grows with the page. */
  scroll?: boolean;
  testId?: string;
}) {
  return (
    <aside className={cn('jw-tools', className)} data-testid={testId}>
      <div className="jw-col-head">
        <div className="jw-col-h">
          <h2>{title}</h2>
          {onCollapse ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-auto"
              aria-label={collapseLabel}
              onClick={onCollapse}
            >
              <ChevronRight strokeWidth={ICON} />
            </Button>
          ) : null}
        </div>
      </div>
      <div className={cn('jw-col-body', scroll && 'jw-scroller')}>{children}</div>
    </aside>
  );
}

/**
 * The closed journal «Værktøjer» strip: a short card with an icon and a
 * vertical label. File view uses the same control for Filer and Markering,
 * with its own icons, so the closed tools do not grow a second chrome.
 */
export function CollapsedToolRail({
  label,
  icon,
  onClick,
  testId,
  className,
  badge,
  expanded,
  chrome = false,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  testId?: string;
  className?: string;
  badge?: ReactNode;
  /** File tools announce that the panel is collapsed. Omit on the journal rail. */
  expanded?: boolean;
  /** File preview ignores clicks on this control when placing a mark. */
  chrome?: boolean;
}) {
  return (
    <button
      type="button"
      className={cn('jw-rail', className)}
      data-testid={testId}
      data-fp-chrome={chrome ? true : undefined}
      aria-label={label}
      aria-expanded={expanded}
      onClick={onClick}
    >
      {icon}
      <span className="jw-vt">
        {label}
        {badge}
      </span>
    </button>
  );
}

export function ToolsSection({
  title,
  icon,
  open,
  defaultOpen = true,
  onToggle,
  count,
  children,
}: {
  title: string;
  icon?: ReactNode;
  /** Controlled open state. Omit to let the section remember its own toggle. */
  open?: boolean;
  defaultOpen?: boolean;
  onToggle?: () => void;
  count?: number;
  children: ReactNode;
}) {
  const [localOpen, setLocalOpen] = useState(defaultOpen);
  const expanded = open ?? localOpen;

  function toggle() {
    onToggle?.();
    if (open === undefined) setLocalOpen((current) => !current);
  }

  return (
    <section className={cn('jw-panel', !expanded && 'closed')}>
      <button type="button" className="jw-ph" aria-expanded={expanded} onClick={toggle}>
        {icon}
        <h3>{title}</h3>
        {count != null && count > 0 ? <span className="cnt num">{count}</span> : null}
        <ChevronDown
          className={cn('chev h-4 w-4', !expanded && 'rotate-[-90deg]')}
          strokeWidth={ICON}
          aria-hidden
        />
      </button>
      <div className="jw-pb">{children}</div>
    </section>
  );
}
